/**
 * TanStack Query hooks for an Account's negotiated Rate Card (RP-12.4).
 *
 * The firm-wide Rate Card (Settings ▸ Rate Card, `@/hooks/use-rate-card.ts`) prices every
 * Account by default. This surface records that ONE Account negotiated a different bill
 * rate for a Labor Category — optionally scoped to a single Rate Basis, or to every basis
 * when `rateBasisId` is null. Response types come from `@/schemas/account-rate-overrides`,
 * the api-engineer's contract for this endpoint.
 *
 * Unlike the firm-wide card (whole-card draft, because Avg Project Mix % must total
 * exactly 100%), each override here is edited and saved as its own row — there is no
 * cross-row invariant to protect, so a mutation touches exactly one override.
 *
 * Mutations invalidate this Account's rate card AND the firm-wide `rateCardKeys` — the
 * Catalog and card headers derive figures that could be open elsewhere while this
 * Account's overrides change.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountKeys, rateCardKeys, resourcePlanKeys } from "@/lib/query";
import type {
  AccountRateCardView,
  AccountRateOverrideView,
  PlanAccountRateCardProposalView,
  SaveAccountRateCardFromPlanResult,
  SolutionPlanRateCardView,
} from "@/schemas/account-rate-overrides";

// Re-export response types so the UI layer only needs one import.
export type {
  AccountRateCardView,
  AccountRateOverrideView,
  PlanAccountRateCardCategoryView,
  PlanAccountRateCardProposalView,
} from "@/schemas/account-rate-overrides";

async function readJsonOrThrow<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Request failed (${res.status})`);
  }
  return (await res.json()) as T;
}

// ── Query ─────────────────────────────────────────────────────────────────────

/**
 * @param tierId Price the card against this Engagement Standard Tier instead of the one
 *   the Account is saved with. The Account page's tier control is local draft state until
 *   Save, so passing the draft tier here lets the card follow the selection immediately.
 *   Omit to use the saved tier.
 * @param basis The Default Rate Card basis selected on the Account page — a Rate Basis id,
 *   or "tier" to follow the tier. Omit to use the saved one.
 */
export function useAccountRateCard(accountId: string, tierId?: string, basis?: string) {
  return useQuery({
    queryKey: accountKeys.accountRateCard(accountId, tierId, basis),
    queryFn: async (): Promise<AccountRateCardView> => {
      const params = new URLSearchParams();
      if (tierId !== undefined) params.set("tierId", tierId);
      if (basis !== undefined) params.set("basis", basis);
      const qs = params.size === 0 ? "" : `?${params.toString()}`;
      return readJsonOrThrow<AccountRateCardView>(
        await fetch(`/api/accounts/${accountId}/rate-card${qs}`),
      );
    },
    // Keep the previous tier's rates on screen while the new tier loads, so switching
    // tiers does not flash the whole table back to skeletons.
    placeholderData: (previous) => previous,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

/** Body of `POST /api/accounts/:id/rate-overrides`. */
export interface AccountRateOverrideInput {
  laborCategoryId: string;
  rateBasisId?: string | null;
  billRate: string;
  effectiveStart: string;
  effectiveEnd?: string | null;
  reason: string;
  sourceDocumentReference?: string | null;
}

/** Body of `PATCH /api/rate-overrides/:overrideId` — everything but the category, optional. */
export type AccountRateOverridePatch = Partial<Omit<AccountRateOverrideInput, "laborCategoryId">>;

function useInvalidateAccountRateCard(accountId: string) {
  const qc = useQueryClient();
  return () => {
    // Prefix key: invalidates every tier variant, not just the saved-tier one.
    qc.invalidateQueries({ queryKey: accountKeys.accountRateCardAll(accountId) });
    qc.invalidateQueries({ queryKey: accountKeys.solutionPlanRateCardAll });
    qc.invalidateQueries({ queryKey: rateCardKeys.all });
  };
}

export function useCreateAccountRateOverride(accountId: string) {
  const invalidate = useInvalidateAccountRateCard(accountId);
  return useMutation({
    mutationFn: async (input: AccountRateOverrideInput) =>
      readJsonOrThrow<AccountRateOverrideView>(
        await fetch(`/api/accounts/${accountId}/rate-overrides`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useUpdateAccountRateOverride(accountId: string) {
  const invalidate = useInvalidateAccountRateCard(accountId);
  return useMutation({
    mutationFn: async (input: { overrideId: string } & AccountRateOverridePatch) => {
      const { overrideId, ...patch } = input;
      return readJsonOrThrow<AccountRateOverrideView>(
        await fetch(`/api/rate-overrides/${overrideId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(patch),
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useDeleteAccountRateOverride(accountId: string) {
  const invalidate = useInvalidateAccountRateCard(accountId);
  return useMutation({
    mutationFn: async (overrideId: string) =>
      readJsonOrThrow<{ ok: boolean }>(
        await fetch(`/api/rate-overrides/${overrideId}`, { method: "DELETE" }),
      ),
    onSuccess: invalidate,
  });
}

// ── Seed an Account's Rate Card from a plan ───────────────────────────────────

/** The plan's rates, one per Labor Category, as they would be saved on its Account. */
export function usePlanAccountRateCard(planId: string) {
  return useQuery({
    queryKey: resourcePlanKeys.accountRateCard(planId),
    queryFn: async (): Promise<PlanAccountRateCardProposalView> =>
      readJsonOrThrow<PlanAccountRateCardProposalView>(
        await fetch(`/api/plans/${planId}/account-rate-card`),
      ),
  });
}

/** Body of `POST /api/plans/:planId/account-rate-card`. */
export interface SaveAccountRateCardFromPlanInput {
  scope: "planBasis" | "allBases";
  effectiveStart: string;
  reason: string;
  sourceDocumentReference?: string | null;
  /** laborCategoryId → bill rate, for each Labor Category the plan prices more than one way. */
  choices?: Record<string, string>;
}

export function useSaveAccountRateCardFromPlan(planId: string, accountId: string) {
  const qc = useQueryClient();
  const invalidateAccount = useInvalidateAccountRateCard(accountId);
  return useMutation({
    mutationFn: async (input: SaveAccountRateCardFromPlanInput) =>
      readJsonOrThrow<SaveAccountRateCardFromPlanResult>(
        await fetch(`/api/plans/${planId}/account-rate-card`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(input),
        }),
      ),
    onSuccess: () => {
      invalidateAccount();
      qc.invalidateQueries({ queryKey: resourcePlanKeys.accountRateCard(planId) });
    },
  });
}

/**
 * Which Rate Card a new plan on this Solution would price off — the New Resource Plan
 * form's "Rates" choice. Idle until a Solution is picked.
 */
export function useSolutionPlanRateCard(solutionId: string) {
  return useQuery({
    queryKey: accountKeys.solutionPlanRateCard(solutionId),
    queryFn: async (): Promise<SolutionPlanRateCardView> =>
      readJsonOrThrow<SolutionPlanRateCardView>(
        await fetch(`/api/solutions/${solutionId}/plan-rate-card`),
      ),
    enabled: solutionId !== "",
  });
}
